import useAuthState from '@/states/auth.state';
import useTenantState from '@/states/tenant.state';
import { axiosInstance } from '@/utils/lib/axios';
import { Spinner } from 'alurkerja-ui';
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { UserService } from '@/api/user';

type ProtectedRouteProps = {
  layout: React.FC;
};

export const ProtectedGlobalRoute: React.FC<ProtectedRouteProps> = ({ layout: Layout }) => {
  const navigate = useNavigate();
  const { loadTenants, tenants, loadUserProfile, isLogoutLoading } = useAuthState();
  const [token, setToken] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const { currentTenant } = useTenantState();
  const { data: userProfileData, isLoading: isProfileLoading } = UserService.useGetProfile();

  // Fetch token async on mount
  useEffect(() => {
    const fetchToken = async () => {
      const authToken = await useAuthState.getState().getToken();
      setToken(authToken);
      setIsInitializing(false);
    };
    fetchToken();
  }, []);
  const roleName = (userProfileData?.role?.name || userProfileData?.tenant_role)?.toLowerCase();
  const location = useLocation();
  const whitelistedPaths = ['/workspaces', '/', '/my-invitation'];

  useEffect(() => {
    const initializeTenants = async () => {
      if (token && !tenants) {
        try {
          await loadTenants();
        } catch (error) {
          console.error('Failed to load tenants:', error);
        }
      }
    };

    const initializeUserProfile = async () => {
      if (token) {
        try {
          await loadUserProfile(token, true);
        } catch (error) {
          console.error('Failed to load user profile:', error);
        }
      }
    };

    initializeTenants();
    initializeUserProfile();
  }, [token, loadTenants, tenants, loadUserProfile]);

  useEffect(() => {
    // Wait until token init is complete before validating
    if (isInitializing) return;

    if (!token) {
      console.error('No token found, redirecting to login');
      navigate('/login');
      return;
    }

    axiosInstance.interceptors.request.use(config => {
      config.headers.Authorization = `Bearer ${token}`;
      config.headers['Content-Type'] = 'application/json';
      return config;
    });

    const currentUrl = window.location.href;

    if (currentUrl.includes('/invitation')) {
      console.log('Invitation link detected — skip redirect');
      return;
    }

    if (whitelistedPaths.includes(location.pathname)) {
      console.log('Whitelisted path detected — skip redirect');
      return;
    }

    if (currentTenant && currentTenant.slug && currentTenant.slug !== 'undefined' && currentTenant.slug !== 'null') {
      axiosInstance.defaults.headers.common['X-ACTIVE-TENANT'] = currentTenant.slug;
      navigate(`/${currentTenant.slug}`);
    } else {
      console.error('Current Tenant is not defined');
      navigate(`/workspaces`);
    }

  }, [token, currentTenant, tenants, isInitializing]);

  if (isLogoutLoading || isProfileLoading) {
    return (
      <div className="fixed flex items-center justify-center w-screen h-screen gap-2">
        <Spinner className="text-primary" />
        {isLogoutLoading ? 'Logout...' : 'Loading...'}
      </div>
    );
  }

  if ((roleName === 'member' || roleName === '') && !whitelistedPaths.includes(location.pathname)) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gray-100">
        <div className="bg-white p-10 rounded-lg shadow-lg text-center">
          <h1 className="text-6xl font-bold text-red-500 mb-4">403</h1>
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">Forbidden Access</h2>
          <p className="text-gray-600 mb-4">Current Role: <span className="font-bold">{roleName || 'No Role'}</span></p>
          <div className="mt-6">
            <button
              onClick={() => window.location.href = '/workspaces'}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              Kembali ke Workspaces
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (token) {
    return (
      <>
        <Layout />

        <ToastContainer />
      </>
    );
  }

  return (
    <div className="fixed flex items-center justify-center w-screen h-screen gap-2">
      <Spinner className="text-primary" />
      Loading...
    </div>
  );
};
